import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Undo2, X, History, Trash2, ArrowRightLeft, CircleCheckBig, Bell, FileText, PlusCircle, HardHat, PencilLine, Receipt, Banknote, CircleAlert, AlertTriangle } from "lucide-react";

/*
 * UndoHistoryPanel —— 操作撤销面板。
 *
 * 需求背景：
 *   原来顶栏只有一个「撤销」按钮，点了会静默按时间倒序逐级回退，用户既看不到"要撤销的是什么"，
 *   也不知道撤销了哪一步。本面板把撤销历史以列表形式可视化：
 *     - 展示最近若干次「可撤销操作」的文本描述（如：将项目X从「地基」推进到「框架」）；
 *     - 每一条后面自带一个「撤销」按钮；无历史时展示缺省图示 + 文案，避免空白弹窗。
 *
 * 设计取舍：
 *   1. 描述文案在 AppContext 的 snapshot 阶段就以三语兜底写入 meta（zh/en/es），
 *      这里只按当前语言取值，避免真正 i18n 的异步翻译链路，也方便后期扩展新的操作类型。
 *   2. 列表严格对应全局 history 栈（仅 projects / reminders 相关操作），不额外造轮子。
 *   3. 撤回语义（保持既有栈式回退，不做"单点删除"）：
 *       点第 k 条 = 回滚到"第 k 次操作发生前"的状态，并丢弃第 k 条及其之后（更新的）全部记录。
 *       由于快照是「整表全量」，这一语义会导致"点中间一条会连同其上更新的若干条一起被撤掉"。
 *       —— 这是栈式 undo 的固有特性，本次改动不改变它，而是：
 *          (a) 点击后先弹二次确认，明确告知"将同时撤销该操作及其后 N 条"；
 *          (b) 撤销完成后**不自动关闭弹窗**，让用户在原地看到列表实时缩短。
 *   4. 确认态用独立 state 管理（confirmIdx 指向 history 的真实下标），
 *      列表随 history prop 变化自动重渲染，撤销后确认态清空即可。
 */

// 每种操作类型对应的图标与配色，纯展示用；未知类型走默认图标兜底。
const KIND_ICON = {
  advance_phase: CircleCheckBig,
  transfer_project: ArrowRightLeft,
  notify_collection: Bell,
  set_contract: FileText,
  mark_invoiced: Receipt,
  mark_paid: Banknote,
  create_project: PlusCircle,
  start_construction: HardHat,
  update_project: PencilLine,
  delete_project: Trash2,
  mark_reminder_read: Bell,
};

const KIND_TINT = {
  delete_project: "text-rose-600 bg-rose-50",
  mark_paid: "text-emerald-600 bg-emerald-50",
  mark_invoiced: "text-indigo-600 bg-indigo-50",
  advance_phase: "text-brand-600 bg-brand-50",
  start_construction: "text-amber-600 bg-amber-50",
};

export function UndoHistoryPanel({ open, onClose, zh, lang, history = [], onUndoAt }) {
  // confirmIdx：当前等待二次确认的 history 真实下标；null 表示未进入确认态。
  const [confirmIdx, setConfirmIdx] = useState(null);
  // 弹窗关闭时清空确认态，避免下次打开残留上一次的确认框。
  useEffect(() => { if (!open) setConfirmIdx(null); }, [open]);
  // 历史变动（撤销后 history 会变短）时，若确认态指向已失效的下标则一并清掉，防止越界。
  useEffect(() => { if (confirmIdx != null && confirmIdx >= history.length) setConfirmIdx(null); }, [history.length, confirmIdx]);

  if (!open) return null;
  const pick = (m) => (m ? (lang === "es" ? m.es : lang === "en" ? m.en : m.zh) : (zh ? "未命名操作" : "Unknown action"));

  // 点击某条「撤销」：不立即执行，先进入确认态。
  const askConfirm = (realIdx) => setConfirmIdx(realIdx);
  // 确认执行：调用父级 onUndoAt（保持既有回退语义），执行后清空确认态但**不关闭弹窗**。
  const doConfirm = () => {
    const idx = confirmIdx;
    if (idx == null) return;
    onUndoAt(idx);
    setConfirmIdx(null);
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999]" data-testid="undo-history-modal">
      <div className="absolute inset-0 bg-slate-900/45 backdrop-blur-[2px]" data-testid="undo-history-backdrop" onClick={onClose} />
      {/*
        弹窗尺寸：
        - 宽度：手机端占满屏宽（左右各留 0.75rem），到 sm 断点后放宽到 42rem（max-w-2xl）以容纳较长描述；
        - 高度：桌面/移动统一用视口高度的 78vh 作为近似「固定高度」（带 78vh 上限 + 最小高度下限），
          这样无论历史多少条，弹窗整体高度稳定、列表区内部滚动，不会因条目增减而抖动。
        注意手机端：用 dvh 兼容移动浏览器地址栏收放，避免 100vh 导致的底部被裁。
      */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-1.5rem)] sm:w-[42rem] sm:max-w-[calc(100vw-2rem)] h-[78dvh] sm:h-[78vh] min-h-[360px] max-h-[calc(100dvh-2rem)] rounded-3xl bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* 面板头：标题 + 关闭 */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center"><History className="h-4 w-4" /></span>
            <div>
              <p className="text-sm font-bold text-slate-800">{zh ? "可撤销的操作记录" : "Undoable Actions"}</p>
              <p className="text-[11px] text-slate-400">{zh ? "可撤销最近的项目 / 提醒操作" : "Undo recent project / reminder actions"}</p>
            </div>
          </div>
          <button type="button" data-testid="undo-history-close" onClick={onClose} className="h-8 w-8 -mr-1 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>

        {/* 列表区 / 缺省态 */}
        <div className="flex-1 overflow-y-auto px-3 py-3" data-testid="undo-history-list">
          {history.length === 0 ? (
            // 缺省态：因弹窗是固定高度，这里需撑满列表区高度并垂直/水平居中，避免内容顶在上方
            <div className="h-full w-full flex flex-col items-center justify-center text-center px-4" data-testid="undo-history-empty">
              {/* 缺省图示：用内联插画（避免依赖外部图片资源），下方配一句提示 */}
              <span className="h-16 w-16 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center"><CircleAlert className="h-7 w-7" /></span>
              <p className="mt-4 text-sm font-semibold text-slate-700">{zh ? "当前没有可撤销的操作" : "Nothing to undo"}</p>
              <p className="mt-1 text-xs text-slate-400 max-w-xs leading-relaxed">{zh ? "当你推进项目阶段、改派团队、标记收付款或标记提醒已读后，这里会记录并可一键撤销。" : "Actions like advancing a project, reassigning a team, marking payments or reading reminders will appear here."}</p>
            </div>
          ) : (
            // 倒序展示：最新的操作在最上方（栈顶优先）
            [...history].reverse().map((entry, revIdx) => {
              const realIdx = history.length - 1 - revIdx; // 对应 history 里的真实下标
              const Icon = KIND_ICON[entry?.meta?.kind] || Undo2;
              const tint = KIND_TINT[entry?.meta?.kind] || "text-slate-600 bg-slate-100";
              const isConfirming = confirmIdx === realIdx;
              // 该条被撤销时，会连同它之后（更新）的所有记录一起回退：数量 = realIdx + 1（含自身）。
              // 换算成"它上面还有几条会一起被撤"：aboveCount = revIdx（倒序里的排位）。
              const aboveCount = revIdx;
              return (
                <div key={realIdx} data-testid={`undo-history-item-${realIdx}`}
                  className="group flex items-start gap-3 rounded-2xl px-2.5 py-2.5 hover:bg-slate-50 transition-colors">
                  <span className={`h-9 w-9 shrink-0 rounded-xl flex items-center justify-center ${tint}`}><Icon className="h-4 w-4" /></span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] text-slate-700 leading-snug break-words">{pick(entry?.meta)}</p>
                    {/* 非栈顶条目：常态给出一句轻提示，避免用户误解为"只撤这一条" */}
                    {aboveCount > 0 && !isConfirming && (
                      <p className="mt-0.5 text-[11px] text-amber-600">{zh ? `撤销此条将同时撤销其上 ${aboveCount} 条更新的操作` : `Undoing this also reverts ${aboveCount} newer action(s) above`}</p>
                    )}
                  </div>
                  <button type="button" data-testid={`undo-history-undo-${realIdx}`}
                    onClick={() => askConfirm(realIdx)}
                    className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-brand-600 hover:text-white hover:bg-brand-500 border border-brand-200 rounded-full px-2.5 py-1 transition-colors">
                    <Undo2 className="h-3.5 w-3.5" />{zh ? "撤销" : "Undo"}
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* 二次确认条：点「撤销」后从底部升起，明确告知影响范围后再执行；执行后不关闭弹窗。
            这样既保留原有栈式回退语义，又避免用户"点中间一条却连带撤掉更新的几条"时毫无预期。 */}
        {confirmIdx != null && (
          <div className="border-t border-slate-100 bg-amber-50/70 px-4 py-3" data-testid="undo-history-confirm">
            <div className="flex items-start gap-2">
              <span className="h-5 w-5 shrink-0 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center"><AlertTriangle className="h-3.5 w-3.5" /></span>
              <p className="flex-1 text-[12px] text-amber-800 leading-relaxed">
                {(() => {
                  const above = history.length - 1 - confirmIdx; // 该条之上（更新）会被一并撤销的条数
                  return above > 0
                    ? (zh ? `此操作会回退到该操作发生之前，并将同时撤销它之上的 ${above} 条更新操作，且无法单独还原。确定继续吗？`
                          : `This reverts to before this action and also undoes ${above} newer action(s) above it. This cannot be undone separately. Continue?`)
                    : (zh ? "将回退到该操作发生之前的状态，确定继续吗？" : "This reverts to the state before this action. Continue?");
                })()}
              </p>
            </div>
            <div className="mt-2 flex justify-end gap-2">
              <button type="button" data-testid="undo-history-confirm-cancel" onClick={() => setConfirmIdx(null)}
                className="text-xs font-semibold text-slate-600 hover:text-slate-800 rounded-full border border-slate-200 px-3 py-1.5">{zh ? "取消" : "Cancel"}</button>
              <button type="button" data-testid="undo-history-confirm-ok" onClick={doConfirm}
                className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-full px-3 py-1.5"><Undo2 className="h-3.5 w-3.5" />{zh ? "确认撤销" : "Confirm undo"}</button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default UndoHistoryPanel;
